import { RyeAPIDetailsCard } from 'components/Orders/OrderDetailsCards/DetailsCard/ryeAPIDetailsCard';
import { StatusDetailsCard } from 'components/Orders/OrderDetailsCards/DetailsCard/statusDetailsCard';
import { PriceDetailsCard } from 'components/Orders/OrderDetailsCards/DetailsCard/priceDetailsCard';
import type {
  AmazonCheckoutRequestFirestore,
  CheckoutRequestFirestore,
  ShopifyOrderRequestProduct,
} from '@rye-com/rye-data-layer';
import { isPermissionlessOrderDetails } from '@rye-com/rye-data-layer/dist/checkoutRequests/shopifyCheckoutRequest';
import './styles.scss';
import { getOrderStatus, OrderTracking } from 'utils/orderStatus';
import { ShippingInfoDetailsCard } from 'components/Orders/OrderDetailsCards/DetailsCard/shippingInfoDetailsCard';
import { getFormattedAddress } from 'utils/formatters';
import { ShopifyItemCatalogCardTable } from './ItemCatalogCard/shopifyItemCatalogCardTable';
import { OrderShipment } from '../../useOrderStatus';

export type ShopifyCheckoutRequest = Exclude<
  CheckoutRequestFirestore,
  AmazonCheckoutRequestFirestore
>;

interface ShopifyOrderDetailsCardTableProps {
  orderDetails: ShopifyCheckoutRequest & {
    createdAtUTS: number;
    request: {
      products: (ShopifyOrderRequestProduct & { price?: number | null })[];
    };
  };
  graphqlStatus?: string;
  graphqlShipments?: OrderShipment[];
}

const convertShipmentsToTracking = (shipments: OrderShipment[]): OrderTracking[] =>
  shipments.map((shipment) => ({
    trackingNumber: shipment.carrierTrackingNumber,
    courierUrl: shipment.carrierTrackingUrl ?? undefined,
    courierName: shipment.carrierName,
  }));

export const ShopifyOrderDetailsCardTable = ({
  orderDetails,
  graphqlStatus,
  graphqlShipments,
}: ShopifyOrderDetailsCardTableProps) => {
  // Use GraphQL data if available, fallback to Firestore-calculated values
  const orderStatus = graphqlStatus ?? getOrderStatus(orderDetails);
  const orderTracking =
    graphqlShipments && graphqlShipments.length > 0
      ? convertShipmentsToTracking(graphqlShipments)
      : orderDetails.order?.fulfillments?.flatMap((x) => x.trackingDetails) ?? [];

  return (
    <div className="card-table-layout">
      <div>
        <StatusDetailsCard orderStatus={orderStatus} orderTracking={orderTracking} />
        <ShippingInfoDetailsCard
          emailAddress={orderDetails.request.shippingAddress.email}
          name={`${orderDetails.request.shippingAddress.firstName} ${orderDetails.request.shippingAddress.lastName}`}
          streetAddress={`${orderDetails.request.shippingAddress.address1}, ${orderDetails.request.shippingAddress.address2}`}
          location={`${getFormattedAddress(
            orderDetails.request.shippingAddress.city,
            orderDetails.request.shippingAddress.province,
          )}`}
          country={orderDetails.request.shippingAddress.country}
        />
        <RyeAPIDetailsCard
          createdOn={new Date(orderDetails.createdAtUTS)}
          orderName={
            orderDetails.order && isPermissionlessOrderDetails(orderDetails.order)
              ? orderDetails.order.orderName
              : 'N/A'
          }
          location={`${getFormattedAddress(
            orderDetails.request.shippingAddress.province,
            orderDetails.request.shippingAddress.country,
          )}`}
          marketplace={orderDetails.marketplace}
        />
      </div>
      <div className="card-table-price-details">
        <ShopifyItemCatalogCardTable
          products={orderDetails.request.products ?? []}
          currency={orderDetails.order?.currency}
        />
        {orderDetails.order?.totalCents && (
          <PriceDetailsCard
            shippingCents={orderDetails.order.shippingCents}
            subtotalCents={orderDetails.order.subtotalCents}
            taxCents={orderDetails.order.taxCents}
            totalCents={orderDetails.order.totalCents}
            currency={orderDetails.order.currency}
          />
        )}
      </div>
    </div>
  );
};
